import { Alpine } from '../../core/alpine-core.js';
import { apiFetch, getAccessToken } from '../api.js';

function parseFixtureId() {
  const parts = location.pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('fixtures');
  if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
  return null;
}

function routerNavigate(path) {
  const router = window.PineconeRouter;
  if (router?.navigate) router.navigate(path);
  else window.location.href = path;
}

function makeDeviceId(fixtureId) {
  try {
    const key = `pdl.fixture-viewer-id.${fixtureId}`;
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const created = `fixture-${fixtureId}-${Math.random().toString(36).slice(2, 10)}-viewer`;
    window.localStorage.setItem(key, created);
    return created;
  } catch {
    return `fixture-${fixtureId}-viewer`;
  }
}

function applyDedicatedChromeMode(on) {
  try {
    window.Alpine?.store?.('ui')?.setHideChrome?.(!!on);
    const shell = document.getElementById('admin');
    if (shell) shell.classList.toggle('layout-scorer-bleed', !!on);
  } catch { }
}

Alpine.data('fixtureViewer', () => ({
  fixtureId: '',
  fixture: null,
  session: null,
  loading: true,
  error: '',
  eventSource: null,
  eventStreamHealthy: false,
  heartbeatTimer: null,
  deviceId: '',
  deviceLabel: '',
  fullscreenActive: false,

  async init() {
    if (!this.$store.features.enabled('liveFixtures')) {
      this.error = 'Live fixture scoring is disabled.';
      this.loading = false;
      return;
    }
    this.fixtureId = parseFixtureId() || '';
    applyDedicatedChromeMode(true);
    this.deviceId = makeDeviceId(this.fixtureId);
    this.deviceLabel = `${navigator.platform || 'Device'} viewer`;
    this.handleFullscreenChange = () => {
      this.fullscreenActive = !!document.fullscreenElement;
    };
    window.addEventListener('beforeunload', () => this.teardown());
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    try {
      await this.loadFixture();
      await this.loadSession();
      this.ensureEventStream();
      this.ensureHeartbeatLoop();
    } catch (e) {
      this.error = e.message || 'Unable to load live viewer';
    } finally {
      this.loading = false;
    }
  },

  teardown() {
    applyDedicatedChromeMode(false);
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.handleFullscreenChange) {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      this.handleFullscreenChange = null;
    }
  },

  async loadFixture() {
    const res = await apiFetch(`/fixtures/${this.fixtureId}?populate=true`);
    const data = await res.json();
    if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to load fixture');
    this.fixture = data.fixture;
  },

  async loadSession() {
    const res = await apiFetch(`/fixtures/${this.fixtureId}/session`);
    const data = await res.json();
    if (!res.ok || !data?.ok || !data?.session?._id) throw new Error(data?.message || 'No live fixture session found');
    this.session = data.session;
  },

  sessionEventUrl() {
    const token = encodeURIComponent(getAccessToken() || '');
    const apiBase = String(window.__API_BASE__ || '').replace(/\/+$/, '');
    const path = `/api/fixture-sessions/${this.session?._id}/events?access_token=${token}&deviceId=${encodeURIComponent(this.deviceId)}&deviceLabel=${encodeURIComponent(this.deviceLabel)}&mode=viewer`;
    return apiBase ? `${apiBase}${path}` : path;
  },

  ensureEventStream() {
    if (!this.session?._id || this.eventSource || !window.EventSource) return;
    this.eventSource = new EventSource(this.sessionEventUrl());
    const handler = (event) => {
      const payload = JSON.parse(event.data || '{}');
      this.eventStreamHealthy = true;
      if (payload?.session) this.session = payload.session;
    };
    for (const evt of ['session:update', 'session:created', 'session:current-game', 'session:players', 'session:throw', 'session:undo', 'session:leg', 'session:game', 'session:scoresheet', 'session:submitted', 'session:verified', 'session:control']) {
      this.eventSource.addEventListener(evt, handler);
    }
    this.eventSource.onopen = () => {
      this.eventStreamHealthy = true;
    };
    this.eventSource.onerror = () => {
      this.eventStreamHealthy = false;
    };
  },

  ensureHeartbeatLoop() {
    if (this.heartbeatTimer || !this.session?._id) return;
    this.heartbeatTimer = window.setInterval(async () => {
      try {
        await apiFetch(`/fixture-sessions/${this.session._id}/heartbeat`, {
          method: 'POST',
          body: {
            deviceId: this.deviceId,
            deviceLabel: this.deviceLabel,
            mode: 'viewer',
          },
        });
      } catch {
        this.eventStreamHealthy = false;
      }
    }, 10_000);
  },

  currentGame() {
    return this.session?.currentGame || this.session?.games?.[Number(this.session?.currentGameIndex || 0)] || null;
  },

  currentLeg() {
    const game = this.currentGame();
    return game?.legs?.[game.legs.length - 1] || null;
  },

  gameLabel(game) {
    const type = String(game?.type || 'singles');
    if (type === 'beer') return `${game.order}. Beer Leg`;
    return `${game.order}. ${type.replace(/^./, (c) => c.toUpperCase())}`;
  },

  teamName(side) {
    return side === 'home' ? (this.fixture?.homeTeamName || 'Home') : (this.fixture?.awayTeamName || 'Away');
  },

  playerName(playerId) {
    const home = [...(this.fixture?.home?.players?.roster || []), ...(this.fixture?.home?.players?.reserves || [])];
    const away = [...(this.fixture?.away?.players?.roster || []), ...(this.fixture?.away?.players?.reserves || [])];
    const player = [...home, ...away].find((item) => String(item?._id || '') === String(playerId || ''));
    return player?.fullName || player?.nickname || '';
  },

  recentEvents() {
    return [...(this.session?.eventLog || [])].slice(-12).reverse();
  },

  openScorer() {
    routerNavigate(`/admin/fixtures/${this.fixtureId}/setup`);
  },

  async toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch { }
  },

  close() {
    this.teardown();
    routerNavigate(`/admin/fixtures/${this.fixtureId}`);
  },
}));
